/**
 * User API Service
 * Centralized service for user role and admin API validation
 * Checks if user is super admin and if Send To Admin API is enabled
 */

import { BASE_URL } from "../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../API_Endpoints/APIEndpoints";

export interface UserInfo {
  userId: string;
  email: string;
  isSuperAdmin: boolean;
  userRoles?: Array<{ name: string; [key: string]: any }>;
  [key: string]: any;
}

/**
 * Fetch user details including super admin status
 * @returns Promise<UserInfo | null>
 */
export const getUserDetails = async (): Promise<UserInfo | null> => {
  try {
    const userData = getUserIdFromLocalStorage();

    if (!userData || !userData.token || !userData.userId) {
      console.log("❌ No user data available");
      return null;
    }

    const { userId, token, organizationId } = userData;
    const sessionToken = localStorage.getItem("sessionToken");

    const response = await fetch(`${BASE_URL}/users/${userId}/${organizationId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(sessionToken && { "X-Session-Token": sessionToken }),
      },
    });

    if (response.ok) {
      const user = await response.json();
      return user;
    }

    console.error("Failed to fetch user details:", response.statusText);
    return null;
  } catch (error) {
    console.error("Error fetching user details:", error);
    return null;
  }
};

/**
 * Check if current user is a super admin
 * @returns Promise<boolean>
 */
export const isSuperAdmin = async (): Promise<boolean> => {
  try {
    const user = await getUserDetails();
    const superAdminStatus = user?.isSuperAdmin || false;
    console.log(`✅ Super Admin status: ${superAdminStatus}`);
    return superAdminStatus;
  } catch (error) {
    console.error("Error checking super admin status:", error);
    return false;
  }
};

/**
 * Check if Send To Admin API is enabled in organization settings
 * @returns Promise<boolean>
 */
export const isAdminApiEnabled = async (): Promise<boolean> => {
  try {
    const userData = getUserIdFromLocalStorage();

    if (!userData || !userData.token || !userData.userId || !userData.organizationId) {
      console.log("❌ No user data available for Admin API check");
      return false;
    }

    const { userId, organizationId, token } = userData;

    const response = await fetch(
      `${BASE_URL}/OrangizationConfiguration/${userId}/${organizationId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.ok) {
      const data = await response.json();

      // Find the current organization
      let currentOrg = data.find((org: any) => org.id === organizationId);
      if (!currentOrg && data.length > 0) {
        currentOrg = data[0];
      }

      if (currentOrg) {
        const isEnabled =
          currentOrg.sendToAdminURL === "true" || currentOrg.sendToAdminURL === true;
        console.log(`✅ Admin API enabled: ${isEnabled}`);
        return isEnabled;
      }
    }

    console.log("❌ Admin API not enabled or organization not found");
    return false;
  } catch (error) {
    console.error("❌ Error checking Admin API status:", error);
    return false;
  }
};

/**
 * Get organization configuration details
 * @returns Promise with organization config or null
 */
export const getOrganizationConfig = async (): Promise<any | null> => {
  try {
    const userData = getUserIdFromLocalStorage();

    if (!userData || !userData.token || !userData.userId || !userData.organizationId) {
      return null;
    }

    const { userId, organizationId, token } = userData;

    const response = await fetch(
      `${BASE_URL}/OrangizationConfiguration/${userId}/${organizationId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.ok) {
      const data = await response.json();

      // Find the current organization
      let currentOrg = data.find((org: any) => org.id === organizationId);
      if (!currentOrg && data.length > 0) {
        currentOrg = data[0];
      }

      return currentOrg;
    }

    return null;
  } catch (error) {
    console.error("Error fetching organization config:", error);
    return null;
  }
};

/**
 * Get Comply Admin URL from organization settings
 * @returns Promise<string> - Comply Admin URL or empty string
 */
export const getComplyAdminUrl = async (): Promise<string> => {
  const config = await getOrganizationConfig();
  return config?.complyAdminUrl || "";
};

/**
 * Get auto-suspend days setting
 * @returns Promise<number> - Number of days or 0 if disabled
 */
export const getAutoSuspendDays = async (): Promise<number> => {
  const config = await getOrganizationConfig();
  return config?.autoSuspendUserAfterDays || 0;
};

/**
 * Check if Admin API should be used based on user role and organization settings
 * Super admins always have access, other users need Send To Admin API enabled
 * @returns Promise<boolean> - true if admin API should be used
 */
export const shouldUseAdminApi = async (): Promise<boolean> => {
  try {
    // First check if user is super admin
    const superAdmin = await isSuperAdmin();

    if (superAdmin) {
      console.log("✅ Super Admin - Admin API access granted");
      return true;
    }

    // For non-super admins, check organization settings
    const isEnabled = await isAdminApiEnabled();

    if (!isEnabled) {
      console.log("⚠️ Send To Admin API is disabled - skipping admin API calls");
      return false;
    }

    console.log("✅ Send To Admin API is enabled");
    return true;
  } catch (error) {
    console.error("❌ Error checking admin API access:", error);
    return false;
  }
};

export default {
  getUserDetails,
  isSuperAdmin,
  isAdminApiEnabled,
  getOrganizationConfig,
  getComplyAdminUrl,
  getAutoSuspendDays,
  shouldUseAdminApi,
};
